/**
 * Precipitation-intensity legend stops for both radar sources (M003 phase 2:
 * US NOAA composite + DWD Germany).
 * M006: surowe progi DWD przeniesione do src/lib/legendStops.mjs (jedno źródło
 * prawdy współdzielone z silnikami renderowania Node) — ten moduł je re-eksportuje
 * w dotychczasowym kształcie.
 */
import { DWD_REFLECTIVITY_STOPS_RAW, DWD_RV_RATE_STOPS_RAW } from "./legendStops.mjs";

export interface LegendStop {
  color: string;
  /** Lower-bound dBZ this stop represents. Only present for sources that publish real classified thresholds (DWD); omitted for the US continuous composite. */
  dbz?: number;
}

/**
 * ---- US (NOAA/NWS ArcGIS + NCEP WMS composite) ----
 * The ImageServer's `/legend?f=pjson` endpoint reports
 * `legendType: "RGB Composite"` with no dBZ → color mapping: the service
 * returns pre-rendered continuous RGB imagery, not a classified raster.
 * The stops below were therefore SAMPLED from live exportImage mosaics of
 * this exact service (pixel-frequency analysis across three frames,
 * 2026-07-19; golden #ffce00 confirmed in an independent review sample the
 * same day). Weak echoes render as desaturated blues, moderate as teals and
 * greens, strong as golds. Higher-intensity colors (orange/red) were not
 * observed in the sampled scenes and are deliberately NOT included — per
 * DESIGN.md §7 we do not invent thresholds or swatches the source has not
 * been seen to produce.
 */
export const REFLECTIVITY_LEGEND_STOPS: LegendStop[] = [
  { color: "#4466aa" },
  { color: "#5588bb" },
  { color: "#55aabb" },
  { color: "#55ccbb" },
  { color: "#44dd88" },
  { color: "#22cc44" },
  { color: "#11bb22" },
  { color: "#00aa11" },
  { color: "#ffce00" }
];

/** CSS gradient string matching the sampled stops (used by the collapsed bar). */
export const LEGEND_GRADIENT_CSS = `linear-gradient(to right, ${REFLECTIVITY_LEGEND_STOPS.map(
  (s) => s.color
).join(", ")})`;

export const LEGEND_SCALE_TITLE = "Intensywność ech radarowych";

export const LEGEND_SOURCE_NOTE =
  "Kolory odwzorowane z próbek bieżącego kompozytu NOAA/NWS (skala ciągła; serwis nie publikuje progów dBZ).";

/**
 * EXACT server values — fetched live from GetStyles (2026-07-19):
 * `maps.dwd.de/geoserver/dwd/wms?service=WMS&version=1.1.1&request=GetStyles
 *  &layers=dwd:Radar_wn-analysis_1x1km_ger`, style "reflectivity".
 * Each entry is the LOWER bound of its dBZ class per the SLD labels
 * ("[7,0 … 9,5)" etc.). Skipped: no-data (-70 → #7d7d7d, op. 0.5) and the
 * fully transparent <7 dBZ class. Nothing here is invented or interpolated.
 */
export const DWD_REFLECTIVITY_STOPS: LegendStop[] = DWD_REFLECTIVITY_STOPS_RAW.map(
  ({ dbz, color }) => ({ dbz, color })
);

export const DWD_LEGEND_GRADIENT_CSS = `linear-gradient(to right, ${DWD_REFLECTIVITY_STOPS.map(
  (s) => s.color
).join(", ")})`;

export const DWD_LEGEND_SCALE_TITLE = "Intensywność ech radarowych (dBZ)";

export const DWD_LEGEND_UNIT = "dBZ";

export const DWD_LEGEND_SOURCE_NOTE = "Dane: DWD, jednostka dBZ.";

/**
 * ---- Wspólna laboratoryjna legenda RATE (mm/h), M006 ----
 * DOKŁADNE kolory i granice klas z oficjalnego stylu opadowego DWD (GetStyles,
 * produkt RV na publicznym WMS, 2026-07-22; fixture-dowód:
 * test/fixtures/rendering/dwd-rv-getstyles.sld). Wpisy = DOLNA granica klasy
 * w mm/h; <0,1 mm/h przezroczyste. Nic nie jest wymyślone. Używana wyłącznie
 * dla źródeł o wielkości fizycznej precipitation-rate (nigdy dla dBZ).
 */
export interface RateLegendStop {
  color: string;
  /** Dolna granica klasy w mm/h. */
  rate: number;
}

export const DWD_RV_RATE_STOPS: RateLegendStop[] = DWD_RV_RATE_STOPS_RAW.map(
  ({ rate, color }) => ({ rate, color })
);

export const DWD_RV_LEGEND_GRADIENT_CSS = `linear-gradient(to right, ${DWD_RV_RATE_STOPS.map(
  (s) => s.color
).join(", ")})`;

export const DWD_RV_LEGEND_SCALE_TITLE = "Intensywność opadu (mm/h)";

export const DWD_RV_LEGEND_UNIT = "mm/h";

export const DWD_RV_LEGEND_SOURCE_NOTE =
  "Rampa: oficjalny styl opadowy DWD (GetStyles, produkt RV) — wspólna legenda laboratoryjna RATE.";
